import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { BehaviorSubject, forkJoin, take } from 'rxjs';
import { DataService } from 'src/app/services/data/data.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { DailyReport } from 'src/app/services/types/daily-report.model';
import { FileMeta } from 'src/app/services/types/file-meta.model';
import { JobDetails } from 'src/app/services/types/job-details.model';
import { OSP } from 'src/app/services/types/osp.model';
@Component({
  selector: 'manager-uploader',
  templateUrl: './manager-uploader.page.html',
  styleUrls: ['./manager-uploader.page.scss'],
  providers: [DataService, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManagerUploaderPage implements OnInit {
  @ViewChild('fileDropRef') profileUrl;
  ospsLoaded: boolean = false;
  filesAreLoading$ = new BehaviorSubject<boolean>(false);
  uploading$ = new BehaviorSubject<boolean>(false);

  ospList: OSP[];
  CPID?: number;
  CPName?: string;

  guid: string = 'pett1grew';
  managerId: string;
  ospId?: string;
  selectedOspId?: number;
  selectedJob?: OSP;

  uploadedFiles: FileMeta[];
  drId?: number;
  cpId?: number;
  title?: string;

  shown?: string;

  get managementFiles(): FileMeta[] {
    return this.uploadedFiles?.filter((file) => file.from != 'Field');
  }
  get fieldFiles(): FileMeta[] {
    return this.uploadedFiles?.filter((file) => file.from == 'Field');
  }

  // LH-35: replaced on load by the Category choices from Quickbase; this list is
  // only the fallback if that request fails.
  categories = [
    'Redline map',
    'Cutsheet',
    'Picture',
    'Other',
    'RoadMap',
    'Client SOW',
    'Permit',
  ];
  files: any[] = [];

  getJobName(dr: DailyReport): string {
    const filledJob = this.ospList?.find(
      (job) => job.recordId == dr.relatedOSP
    );
    return filledJob?.jobName;
  }

  constructor(
    private dataService: DataService,
    private datePipe: DatePipe,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    public modalController: ModalController,
    private activatedRoute: ActivatedRoute
  ) {
    this.route.params.subscribe((params) => {
      if (params['guid']) {
        this.guid = params['guid'];
        if (!this.guid) {
          return;
        }

        this.getOspFiles();
      }
    });

    this.activatedRoute.queryParams
      .pipe(take(1))
      .subscribe((params: Params) => {
        if (params['managerId']) {
          this.managerId = params['managerId'];
        }
      });
  }

  getOspFiles() {
    this.filesAreLoading$.next(true);
    this.dataService
      .getFilesOspGuid(this.guid)
      .pipe(take(1))
      .subscribe((data) => {
        let files: FileMeta[] = [];

        if (!data || data['success'] == false) {
          this.uploadedFiles = files;
          this.filesAreLoading$.next(false);
          return;
        }

        (data['data'] as []).forEach((item) => {
          files.push(new FileMeta(item));
        });

        this.uploadedFiles = files;
        this.filesAreLoading$.next(false);
      }, (error)=>{
        console.log(error);
        this.filesAreLoading$.next(false);
        this.toastService.presentToast(
          'Error',
          'Error while getting files',
          'top',
          'danger',
          4000
        );
      });
  }

  getCPData() {
    this.dataService.getCPDataByGuid(this.guid).subscribe((data) => {
      if (!data) {
        return;
      }

      this.CPID = data[0][3].value;
      this.CPName = data[0][8].value;

      this.cd.markForCheck();
    }, (error)=>{
      console.log(error);

      this.toastService.presentToast(
        'Error',
        'Error while getting CP data',
        'top',
        'danger',
        4000
      );
    });
  }

  ngOnInit() {
    this.loadCategories();
  }

  loadCategories() {
    this.dataService
      .getFileCategories()
      .pipe(take(1))
      .subscribe({
        next: (categories) => {
          if (Array.isArray(categories) && categories.length > 0) {
            this.categories = categories;
            this.cd.markForCheck();
          }
        },
        // keep the built-in list if Quickbase can't be reached
        error: () => {},
      });
  }

  onJobChange(event) {
    this.selectedJob = event.value as JobDetails;
    this.selectedOspId = this.selectedJob.recordId;
    this.cd.markForCheck();
  }

  transformDate(date: string) {
    const dateString = this.datePipe.transform(date, 'MM-dd-yyyy');
    return dateString;
  }

  getOspAttachmentColor() {
    if (this.selectedJob?.withAttachments) return 'darkblue';

    return '';
  }

  getDRAttachmentColor(dr: DailyReport) {
    if (dr?.withAttachments) return 'darkblue';

    return '';
  }

  showAcc(acc: string) {
    if (!this.shown) {
      this.shown = acc;
      return;
    }

    if (this.shown == acc) {
      this.shown = undefined;
      return;
    }

    this.shown = acc;
    return;
  }

  confirm() {
    let hasError = false;
    if (this.files && this.files.length > 0) {
      this.uploading$.next(true);
    }

    const transactions = this.files.map((file) => {
      const formData = new FormData();
      formData.append(file.name, file);
      if (file.category) formData.append('category', file.category);
      if (file.description) formData.append('description', file.description);
      formData.append('uploadedFromWhere', 'Management');
      formData.append('uploadingManagerId', this.managerId);

      return this.dataService.postFileOSPGuid(this.guid, formData);
    });

    forkJoin(...transactions).subscribe((results) => {
      this.uploading$.next(false);

      results.forEach((res) => {
        if (!res || res['success'] != true) {
          hasError = true;
          this.toastService.presentToast(
            'Error',
            'Error while saving',
            'top',
            'danger',
            4000
          );
        }
      });

      if (!hasError) {
        this.files = [];

        this.toastService.presentToast(
          'Success',
          `Uploaded`,
          'top',
          'success',
          4000
        );
      }

      this.getOspFiles();
    }, (error)=>{
      console.log(error);
      this.uploading$.next(false);
      this.toastService.presentToast(
        'Error',
        'Error while saving',
        'top',
        'danger',
        4000
      );
    });
  }

  /**
   * on file drop handler
   */
  onFileDropped($event: File[]) {
    this.prepareFilesList($event);
  }

  /**
   * handle file from browsing
   */
  fileBrowseHandler(files) {
    this.prepareFilesList(files);
  }

  /**
   * Delete file from files list
   * @param index (File index)
   */
  deleteFile(index: number) {
    if (this.uploading$.getValue() != true) {
      this.files.splice(index, 1);
      this.profileUrl.nativeElement.value = '';
    }
  }

  /**
   * Convert Files list to normal array list
   * @param files (Files List)
   */
  prepareFilesList(files: File[]) {
    for (const item of files) {
      this.files.push(item);
    }
  }

  /**
   * format bytes
   * @param bytes (File size in bytes)
   * @param decimals (Decimals point)
   */
  formatBytes(bytes, decimals) {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  downloadFile(fileName, src) {
    let link = document.createElement('a');
    link.setAttribute('type', 'hidden');
    link.href = src;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  fileTriggerName(by: string, file: FileMeta) {
    return `${by}-${file.name}-${file.rid}`;
  }
}
